import { Component, OnInit, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';

import { SystemSettingsService } from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import {
  CustomResultOfPaginatedListOfGetSystemSettingsWithPaginationDto,
  SettingDto,
  SettingWithPaginationDto,
  UpdateSettingCommand,
} from '../../../core/api/models';
import { PaginatedListOfSettingWithPaginationDto } from '../../../core/api/models/paginated-list-of-setting-with-pagination-dto';
import { AppDomService } from '../../../core/services/dom.service';
import { BaseIndexPageComponent } from '../../shared/base/components/base-index-page.component';
import { GetEnumValuesDto } from './../../../core/api/models/get-enum-values-dto';
import { FormPageConfig } from './../../../core/models/form-page-config.model';
import { SystemSettingFormComponent } from './system-setting-form/system-setting-form.component';
import { PageNames } from 'src/app/configs/page-names.enum';
import { BaseIndexModule } from '../../shared/base/modules/base-index.module';

@Component({
    selector: 'app-system-setting',
    templateUrl: './system-setting.component.html',
    imports: [
        SystemSettingFormComponent,
        BaseIndexModule
    ]
})
export class SystemSettingComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region Properties

  pageName = PageNames.systemSetting;
  protected currentService = inject(SystemSettingsService);

  // Binding service endpoints
  paginationApiMethodName: string = this.currentService.systemSettingsGetSystemSettingsWithPagination.name;
  createGetApiMethodName: string = this.currentService.systemSettingsCreateGet.name;
  auditApiMethodName: string = this.currentService.systemSettingsGetSystemSettingsAudit.name;
  deleteApiMethodName: string = this.currentService.systemSettingsDelete.name;
  
  modelWithPagination$: Observable<PaginatedListOfSettingWithPaginationDto>;
  items$: Observable<Array<SettingWithPaginationDto>>;
  formPageConfig: FormPageConfig<SettingDto> = {};
  updateModel: UpdateSettingCommand = {};

  MainSettingsEnumValues: GetEnumValuesDto[] = [];

  //#endregion

  constructor() {
    super();
  }

  ngOnInit() {
    try {
      super.ngOnInit();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  getEnumsValues() {
    this.getEnumValuesApi('SystemMainSettings')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.MainSettingsEnumValues = values));
  }

  configTableColumns() {
    this.tableColumns = [
      {
        title: this.localizer.key,
        name: 'key',
        hasSearch: true,
        type: 'enum',
        enumName: 'SystemMainSettings',
      },
      { title: this.localizer.value, name: 'value', hasSearch: true },
      {
        title: this.localizer.createDate,
        name: 'created',
        hasSearch: true,
        type: 'dateTime',
      },
    ];
  }

  //#region Api

  //Get ById
  baseGetByIdApi(id: number): void {
    this.unSubs();

    this.pageConfig.loading = true;

    this.formPageConfig.editId = id;

    this.currentService
      .systemSettingsUpdateGet({ Id: id })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseGetByIdObserverFormModal);
  }

  //#endregion

  openCreateFormModal() {
    try {
      this.unSubs();

      if (!this.currentUserInfo?.isSuperAdmin) return;

      let data = {
        title: `${this.formPageConfig?.vocabs}addTitle`,
        isEdit: false,
        pageVocab: 'pages.systemSetting.form.',
        isSuperAdmin: this.currentUserInfo?.isSuperAdmin,
      };

      const modalResult = this.dialogService.openModal({
        component: SystemSettingFormComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => (this.pageConfig.isVisibleForm = undefined)),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.getTableData();
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  openEditFormModal(model: SettingWithPaginationDto) {
    try {
      this.unSubs();
      this.updateModel.id = model.id;
      this.updateModel.value = model.value;

      let data = {
        title: `${this.formPageConfig?.vocabs}editTitle`,
        model: {
          id: this.updateModel.id,
          value: this.updateModel.value,
          key: model.key,
        },
        isEdit: true,
        pageVocab: 'pages.systemSetting.form.',
      };

      const modalResult = this.dialogService.openModal({
        component: SystemSettingFormComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.getTableData();
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }
  onDelete(id: any, content: any) {
    const dialogResult = this.getConfirmDialog({
      content: this.MainSettingsEnumValues.find((a) => a.key == content).value,
      isConfirmDelete: true,
      confirmByWritingText: false,
    });

    dialogResult
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.pageConfig.loading = true;
          this.baseDeleteApi(id);
        }
      });
  }
}
